import React, { useState, ReactNode } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Button, Accordion, AccordionHeader, AccordionBody } from "@material-tailwind/react";
import { formatTime } from '../../utils/formatTime';
import { getStatusBadgeText, statusTextColorMap, StatusType } from '../../types/types';
import type { PodMetric } from '../../store/slices/podMetricsSlice';
import { Warning20Filled, ErrorCircle20Filled, CheckmarkCircle20Filled, WrenchFilled, CheckmarkCircle32Filled, Warning32Filled, ErrorCircleFilled, ChevronDown16Filled, ChevronUp16Filled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

interface HistoricalMetricsDialogProps {
  open: boolean;
  handleOpen: () => void;
  data?: {
    containerName: string;
    podName?: string;
    metrics: PodMetric[];
    date: Date;
  };
}

// Helper function to get status icon
const getStatusIcon = (phase: string) => {
  switch (phase) {
    case 'Pending':
      return <Warning32Filled color="#FDB022" />;
    case 'Failed':
      return <ErrorCircleFilled className="w-[32px] h-[32px]" color="#F04438" />;
    case 'Maintenance':
      return <WrenchFilled className="w-[32px] h-[32px]" color="#6172F3" />;
    default:
      return <CheckmarkCircle32Filled color="#00B25D" />;
  }
};

const getStatusIconMini = (phase: string) => {
  switch (phase) {
    case 'Pending':
      return <Warning20Filled color="#FDB022" />;
    case 'Failed':
      return <ErrorCircle20Filled color="#F04438" />;
    case 'Maintenance':
      return <WrenchFilled color="#6172F3" />;
    default:
      return <CheckmarkCircle20Filled color="#00B25D" />;
  }
};

// Custom icon component for accordion
function Icon({ open }: { id: string; open: boolean }): ReactNode {
  return (
    <div className="flex items-center justify-center">
      {open ? <ChevronUp16Filled /> : <ChevronDown16Filled />}
    </div>
  );
}

const HistoricalMetricsDialog: React.FC<HistoricalMetricsDialogProps> = ({ open, handleOpen, data }) => {
  const { t } = useTranslation();
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  
  if (!data) {
    return null;
  }

  const { containerName, podName, metrics, date } = data;
  
  // Ensure metrics is always an array
  const metricsArray = Array.isArray(metrics) ? metrics : [];
  
  // Get the latest metrics
  const sortedMetrics = [...metricsArray].sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  // Format date for display
  const formattedDate = date.toLocaleDateString('default', { 
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  // Get current phase safely
  const currentPhase = sortedMetrics.length > 0 ? getStatusBadgeText(sortedMetrics[0].phase) : 'Unknown';
  
  // Determine border color class
  const borderColorClass = sortedMetrics.length > 0 ? 
    (currentPhase === 'Failed' ? 'border-red-500' : 
     currentPhase === 'Pending' ? 'border-yellow-500' : 
     'border-green-500') : 'border-green-500';

  const handleAccordionOpen = (value: string) => {
    setOpenAccordion(openAccordion === value ? null : value);
  };

  return (
    <Dialog 
      open={open} 
      handler={handleOpen}
      size="md"
      className="bg-white rounded-lg shadow-xl"
      placeholder={undefined}
      onResize={undefined}
      onResizeCapture={undefined}
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
    >
      <DialogHeader 
        className="flex items-center justify-between pb-4"
        placeholder={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        <div className="flex items-start">
          <div className={`mr-4 rounded-md w-12 h-12 border ${borderColorClass} border-1 flex items-center justify-center`}>
            <div>
              {getStatusIcon(currentPhase)}
            </div>
          </div>
          <div>
            <h3 className="font-inter font-bold text-2xl text-[#0C111D]">
              {podName ? podName : metrics[0]?.display_name || containerName}
            </h3>
            <p className="font-inter font-semibold text-sm text-gray-500">
              {formattedDate} <span className={`font-inter font-semibold text-base ${statusTextColorMap[currentPhase as StatusType]}`}>
                {currentPhase}
              </span>
            </p>
          </div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="mr-3 h-5 w-5 cursor-pointer"
          onClick={handleOpen}
        >
          <path
            fillRule="evenodd"
            d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </DialogHeader>
      
      <DialogBody
        className="max-h-[60vh] overflow-y-auto"
        placeholder={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        {podName ? (
          // Show detailed metrics for a single pod
          <div className="relative">
            <div className="mb-6">
              
              <div>
                {sortedMetrics.map((metric, index) => (
                  <div key={index} className="mb-4 border-b border-gray-200 pb-2">
                    <div className="flex justify-between items-center">
                      <p className="font-inter text-base text-gray-800 font-semibold">{formatTime(metric.timestamp)}</p>
                      <div className={`flex items-center ${statusTextColorMap[metric.phase as StatusType] || 'text-green-500'}`}>
                        {getStatusIconMini(metric.phase)}
                      </div>
                    </div>
                    <div className="font-inter text-sm text-gray-600">
                      {formatTime(metric.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          // Show summary of all pods in the container
          <div className="relative">
            <h4 className="font-inter font-medium text-lg text-[#0C111D] mb-4 ml-1">
              {podName ? 'Pod History' : 'Historical Issues'}
            </h4>
            
            {/* Filter out Running status if showing container view */}
            {sortedMetrics.length > 0 && 
              (podName 
                ? Object.entries(
                    sortedMetrics.reduce<Record<string, PodMetric>>((acc, metric) => {
                      if (!acc[metric.pod_name] || new Date(acc[metric.pod_name].timestamp) < new Date(metric.timestamp)) {
                        acc[metric.pod_name] = metric;
                      }
                      return acc;
                    }, {})
                  )
                : sortedMetrics
                    .filter(metric => metric.phase !== 'Running' && metric.log_capture && metric.log_capture.log_lines && metric.log_capture.log_lines.length > 0) // Filter out Running status for container view
                    .map((metric, index) => {
                      const logId = `log-${index}`;
                      
                      return (
                        <Accordion 
                          key={logId} 
                          open={openAccordion === logId}
                          className="mb-4 border border-gray-200 rounded-lg overflow-hidden focus:border-gray-200"
                          icon={<Icon id={logId} open={openAccordion === logId} />}
                          placeholder={undefined}
                          onResize={undefined}
                          onResizeCapture={undefined}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        >
                          <AccordionHeader 
                            onClick={() => handleAccordionOpen(logId)}
                            className="py-3 px-4 flex justify-between items-center hover:bg-gray-50 focus:outline-none focus:shadow-none border-none"
                            placeholder={undefined}
                            onResize={undefined}
                            onResizeCapture={undefined}
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          >
                            <div className="flex flex-col items-start">
                              <div className="font-inter font-medium text-base text-[#0C111D] flex items-center gap-2">
                                {metric.pod_name}
                                <span className={`flex items-center ${statusTextColorMap[metric.phase as StatusType] || 'text-green-500'}`}>
                                  {getStatusIconMini(metric.phase)}
                                </span>
                              </div>
                              <div className="font-inter font-normal text-sm text-gray-600">
                                Creation time: {formatTime(metric.timestamp)}
                              </div>
                            </div>
                          </AccordionHeader>
                          <AccordionBody className="py-2 px-4" placeholder={undefined} onResize={undefined} onResizeCapture={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            {metric.log_capture && metric.log_capture.log_lines && metric.log_capture.log_lines.length > 0 && (
                              <div className="bg-gray-50 rounded-md">
                                <div className="text-xs font-mono bg-gray-900 text-gray-200 p-2 rounded overflow-auto max-h-32">
                                  {metric.log_capture.log_lines.length > 5 && (
                                    <div className="text-gray-400 mb-1">... {metric.log_capture.log_lines.length - 5} earlier lines</div>
                                  )}
                                  {metric.log_capture.log_lines.slice(-5).map((line, i) => (
                                    <div key={i} className="whitespace-pre-wrap">{line}</div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </AccordionBody>
                        </Accordion>
                      );
                    })
            )}
            
            {/* Show message if no problems found */}
            {!podName && sortedMetrics.filter(metric => metric.phase !== 'Running').length === 0 && (
              <div className="text-center py-6 text-gray-500">
                No problem pods found for this container on this day.
              </div>
            )}
          </div>
        )}
      </DialogBody>
      
      <DialogFooter 
        placeholder={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        <Button 
          variant="text"
          color="white"
          onClick={handleOpen}
          className="font-medium opacity-0"
          placeholder={undefined}
          onResize={undefined}
          onResizeCapture={undefined}
          disabled={true}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          {t('Close')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default HistoricalMetricsDialog; 